import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguageTheme } from "../../../../hooks/useLanguageTheme";
import { IStage } from "../../interfaces";

export const TransparentProcessMobileTimeLine = ({
  selectedStage,
  setSelectedStage,
  stages,
}: {
  selectedStage: number;
  setSelectedStage: React.Dispatch<React.SetStateAction<number>>;
  stages: IStage[];
}) => {
  const { i18n } = useTranslation(["sellWithUs"]);
  const isRTL = i18n.dir() === "rtl";

  const swiperRef = useRef<any>(null);
  const stepperContainerRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<any[]>([]);
  const langTheme = useLanguageTheme();

  const hasDraggedRef = useRef<boolean>(false);
  const DRAG_THRESHOLD = 5;

  const handleStepClick = (index: number) => {
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideTo(index);
    }
    setSelectedStage(index);
    if (stepRefs.current[index]) {
      stepRefs.current[index].scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
    }
  };

  const isDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDownRef.current = true;
    hasDraggedRef.current = false;
    if (stepperContainerRef.current) {
      stepperContainerRef.current.classList.add("active");
      startXRef.current = e.pageX - stepperContainerRef.current.offsetLeft;
      scrollLeftRef.current = stepperContainerRef.current.scrollLeft;
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDownRef.current || !stepperContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - stepperContainerRef.current.offsetLeft;
    const walk = x - startXRef.current;
    if (Math.abs(walk) > DRAG_THRESHOLD) {
      hasDraggedRef.current = true;
    }
    stepperContainerRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleMouseLeave = () => {
    isDownRef.current = false;
    if (stepperContainerRef.current) {
      stepperContainerRef.current.classList.remove("active");
    }
  };

  const handleMouseUp = () => {
    isDownRef.current = false;
    if (stepperContainerRef.current) {
      stepperContainerRef.current.classList.remove("active");
    }
  };

  useEffect(() => {
    const stepperEl = stepperContainerRef.current;
    if (!stepperEl) return;
    const onWheel = (evt: WheelEvent) => {
      if (evt.deltaY !== 0) {
        evt.preventDefault();
        let delta = evt.deltaY;
        if (evt.deltaMode === WheelEvent.DOM_DELTA_LINE) {
          delta *= 16;
        } else if (evt.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
          delta *= window.innerHeight;
        }
        stepperEl.scrollLeft += delta * 2;
      }
    };
    const onSelectStart = (e: any) => {
      if (isDownRef.current) {
        e.preventDefault();
      }
    };
    stepperEl.addEventListener("wheel", onWheel, { passive: false });
    stepperEl.addEventListener("selectstart", onSelectStart);
    return () => {
      stepperEl.removeEventListener("wheel", onWheel);
      stepperEl.removeEventListener("selectstart", onSelectStart);
    };
  }, []);

  return (
    <div
      className="step-swiper-container"
      style={{
        fontFamily: langTheme.mainFont(),
      }}
    >
      <div
        className="custom-stepper"
        ref={stepperContainerRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {stages.map((stage, index) => (
          <div
            key={index}
            className={`step-container ${
              index <= selectedStage ? "active" : ""
            }`}
            onClick={() => handleStepClick(index)}
            ref={(el) => (stepRefs.current[index] = el)}
          >
            <div className="mobile-stepper-title-container">
              <div
                className={`step-number ${
                  index <= selectedStage ? "active" : ""
                }`}
              >
                {"0" + Number(index + 1)}
              </div>
              <div
                className="step-label"
                style={{
                  marginRight: isRTL ? 0 : "100px",
                  marginLeft: isRTL ? "100px" : 0,
                  paddingRight: isRTL ? 0 : "100px",
                  paddingLeft: isRTL ? "100px" : 0,
                  maxWidth: !isRTL
                    ? "200px"
                    : isRTL &&
                      (Number(index + 1) === 4 || Number(index + 1) === 5)
                    ? "141px"
                    : "160px",
                }}
              >
                {stage.title}
              </div>
            </div>
            <div className="dot-line-container">
              <div
                className={`dot ${index <= selectedStage ? "active" : ""}`}
              />
              {index < stages.length - 1 && (
                <div
                  className={`line ${
                    index < selectedStage || index === selectedStage
                      ? "active"
                      : ""
                  }`}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          height: "300px",
          position: "relative",
          width: "100%",
          background: `url(${stages[selectedStage]?.image.url}) center center / cover no-repeat`,
        }}
      ></div>
      <div className="step-container-desc">
        {stages[selectedStage]?.description}
      </div>
    </div>
  );
};
